import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { anonymousAuthReady, auth } from '../firebaseConfig';
import contract from '../../functions/usageContract.js';

const { CLIENT_USAGE_TYPES, FAILURE_CODES, FLOWS } = contract;
const SESSION_KEY = 'muulti-usage-session';
let sessionId;
let callable;

export function newUsageId() {
    try {
        if (typeof crypto === 'undefined') return null;
        if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
        if (typeof crypto.getRandomValues !== 'function') return null;
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch { return null; }
}
function usageSession() {
    if (sessionId) return sessionId;
    try {
        const stored = sessionStorage.getItem(SESSION_KEY);
        if (contract.UUID.test(stored)) sessionId = stored;
    } catch { /* Storage can be unavailable; keep a document-local session. */ }
    if (!sessionId) sessionId = newUsageId();
    try { if (sessionId) sessionStorage.setItem(SESSION_KEY, sessionId); } catch { /* Use memory. */ }
    return sessionId;
}
export function usageAttempt(flow) {
    return { flow, attemptId: newUsageId() };
}
export function usageFailureCode(error, fallback = 'other') {
    const codes = {
        'auth/invalid-credential': 'invalid_credentials', 'auth/wrong-password': 'invalid_credentials',
        'auth/user-not-found': 'invalid_credentials', 'auth/user-disabled': 'invalid_credentials',
        'auth/email-already-in-use': 'email_in_use', 'auth/invalid-email': 'invalid_email',
        'auth/weak-password': 'weak_password', 'auth/too-many-requests': 'rate_limited',
        'auth/network-request-failed': 'network', 'functions/unavailable': 'network',
        'functions/resource-exhausted': 'rate_limited', 'functions/cancelled': 'cancelled',
    };
    return codes[error?.code] || (FAILURE_CODES.includes(error?.usageCode) ? error.usageCode : fallback);
}
async function send(input) {
    const currentSession = usageSession();
    if (!currentSession) return;
    try {
        await anonymousAuthReady;
        if (!auth.currentUser) return;
        if (input.action === 'walletLogin' && auth.currentUser.uid !== input.walletUid) return;
        if (!callable) callable = httpsCallable(getFunctions(getApp()), 'usageAnalytics');
        const payload = { ...input, sessionId: currentSession };
        try { await callable(payload); }
        catch (error) {
            // One bounded retry keeps the same identifier; ingestion is idempotent.
            if (!['functions/unavailable', 'functions/deadline-exceeded', 'functions/internal'].includes(error?.code)) throw error;
            await callable(payload);
        }
    } catch {
        // Observability must never block login, wallet creation, or navigation.
        console.warn('Usage event could not be recorded.');
    }
}
export function trackUsageEvent(type, context = {}) {
    if (!CLIENT_USAGE_TYPES.includes(type)) return Promise.resolve();
    const id = newUsageId();
    if (!id) return Promise.resolve();
    return send({ action: 'record', type, id,
        ...(FLOWS.includes(context.flow) ? { flow: context.flow } : {}),
        ...(contract.UUID.test(context.attemptId) ? { attemptId: context.attemptId } : {}),
        ...(FAILURE_CODES.includes(context.failureCode) ? { failureCode: context.failureCode } : {}) });
}
export function reportWalletLogin(walletUid) {
    return send({ action: 'walletLogin', walletUid });
}
